import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DriverDocumentKey } from "../../types/vtc";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES } from "../../data/auth";
import { useDriver } from "../../lib/driverContext";
import { openDriverDocument, uploadDriverDocument } from "../../lib/driverApi";
import type { DriverDocument } from "../../lib/driverApi";
import { errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import { cn, longDate } from "../../lib/format";
import { ActionButton, PageTitle, StatusBadge } from "../../components/company/ui";
import Badge from "../../components/ui/Badge";
import { useT } from "../../i18n";

// Same rules as the backend (driverDocumentsController): the VTC card expires on
// the date in the profile, a proof of address 3 months after upload; "soon" = 30 days
const EXPIRING_SOON_DAYS = 30;
const PROOF_OF_ADDRESS_MONTHS = 3;
const DAY = 24 * 60 * 60 * 1000;

const expiryOf = (doc: DriverDocument, vtcCardExpiry?: string): Date | null => {
  if (doc.key === "vtcCard") return vtcCardExpiry ? new Date(vtcCardExpiry) : null;
  if (doc.key === "proofOfAddress" && doc.uploadedAt) {
    const d = new Date(doc.uploadedAt);
    d.setMonth(d.getMonth() + PROOF_OF_ADDRESS_MONTHS);
    return d;
  }
  return null;
};

/** "expired" / "soon" for an approved document, else null. */
const expiryState = (doc: DriverDocument, expiry: Date | null): "expired" | "soon" | null => {
  if (doc.status !== "approved" || !expiry) return null;
  const left = expiry.getTime() - Date.now();
  return left < 0 ? "expired" : left < EXPIRING_SOON_DAYS * DAY ? "soon" : null;
};

/** "My documents": the 4 files a driver needs before a booking request. */
export default function DriverDocuments() {
  const t = useT();
  const d = t.driverArea.documents;
  const { driver, reload } = useDriver();
  const [uploading, setUploading] = useState<DriverDocumentKey | null>(null);
  if (!driver) return <p className="text-sm text-muted">{t.common.loading}</p>;

  const upload = async (key: DriverDocumentKey, file: File) => {
    if (file.size > DOCUMENT_MAX_BYTES) return toast.error(t.auth.companySignup.fileTooLarge);
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) return toast.error(t.auth.companySignup.fileType);
    setUploading(key);
    try {
      toast.success(await uploadDriverDocument(key, file));
      await reload();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setUploading(null);
    }
  };

  const describe = (doc: DriverDocument, expiry: Date | null) => {
    const file = doc.fileName ?? t.auth.documents[doc.key].label;
    if (doc.status === "missing") return d.notProvided;
    if (doc.status === "rejected") return doc.reviewNote ? d.rejected(doc.reviewNote) : d.rejectedNoNote;
    if (doc.status === "approved") return expiry ? d.approvedUntil(file, longDate(expiry.toISOString().slice(0, 10))) : d.approved(file);
    return doc.uploadedAt ? d.sentOn(file, longDate(doc.uploadedAt)) : file;
  };

  return (
    <>
      <PageTitle className="mb-1.5">{d.title}</PageTitle>
      <p className="m-0 mb-5 max-w-145 text-sm leading-normal text-muted">{d.intro}</p>
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        {driver.documents.map((doc) => {
          const expiry = expiryOf(doc, driver.vtcCardExpiry);
          const state = expiryState(doc, expiry);
          return (
            <DocumentCard
              key={doc.key}
              label={t.auth.documents[doc.key].label}
              text={describe(doc, expiry)}
              status={doc.status}
              badge={
                state && (
                  <Badge tone={state === "expired" ? "red" : "amber"} className="shrink-0 px-1.75 py-0.75 text-[11px]">
                    {state === "expired" ? d.expired : d.expiringSoon}
                  </Badge>
                )
              }
              note={
                state && (
                  <>
                    {d.renewHint}
                    {doc.key === "vtcCard" && (
                      <>
                        {" "}
                        <Link to="/driver/profile" className="text-brand-link">
                          {d.wrongDate}
                        </Link>
                      </>
                    )}
                  </>
                )
              }
              needsAction={doc.status === "missing" || doc.status === "rejected" || !!state}
              buttonLabel={uploading === doc.key ? d.uploading : doc.status === "missing" ? d.upload : d.replace}
              uploading={uploading === doc.key}
              onFile={(file) => void upload(doc.key, file)}
              onView={doc.status === "missing" ? undefined : () => openDriverDocument(doc.key).catch((err) => toast.error(errorText(err)))}
              viewLabel={d.view}
            />
          );
        })}
      </div>
    </>
  );
}

interface DocumentCardProps {
  label: string;
  text: string;
  status: DriverDocument["status"];
  needsAction: boolean;
  buttonLabel: string;
  uploading: boolean;
  onFile: (file: File) => void;
  /** Opens the uploaded file; absent while the document is missing. */
  onView?: () => void;
  viewLabel: string;
  /** Replaces the status badge (e.g. "Expires soon"). */
  badge?: ReactNode;
  /** Extra line under the text (e.g. how to renew). */
  note?: ReactNode;
}

function DocumentCard({
  label,
  text,
  status,
  needsAction,
  buttonLabel,
  uploading,
  onFile,
  onView,
  viewLabel,
  badge,
  note,
}: DocumentCardProps) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <section
      className={cn("flex flex-col gap-2.5 rounded-card border bg-white p-5", needsAction ? "border-[#F1B9B4]" : "border-transparent")}
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="m-0 text-[15px] font-medium">{label}</h2>
        {badge ?? <StatusBadge kind="companyDoc" status={status} className="shrink-0 px-1.75 py-0.75 text-[11px]" />}
      </div>
      <p className="m-0 flex-1 break-words text-[13px] text-muted">
        {text}
        {onView && (
          <>
            {" · "}
            <button
              type="button"
              onClick={onView}
              className="cursor-pointer border-0 bg-transparent p-0 text-[13px] text-brand hover:text-brand-link"
            >
              {viewLabel}
            </button>
          </>
        )}
      </p>
      {note && <p className="m-0 text-xs leading-normal text-tone-amber-fg">{note}</p>}
      <ActionButton
        variant={needsAction ? "primary" : "grey"}
        size="lg"
        className="w-full"
        disabled={uploading}
        onClick={() => input.current?.click()}
      >
        {buttonLabel}
      </ActionButton>
      <input
        ref={input}
        type="file"
        accept={DOCUMENT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = ""; // allow picking the same file again
        }}
      />
    </section>
  );
}
